import React, { useState, useEffect } from 'react';
import { University, UserProfile, AuditLog } from '@/api/entities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Building, Users, Shield, Activity, Plus, Settings, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import StatsCards from '../components/dashboard/StatsCards';
import { useUniversity } from '@/hooks/useUniversity';
import { supabase } from '@/lib/supabase';

export default function SuperAdminDashboard() {
  const { activeUniversity } = useUniversity();
  const [stats, setStats] = useState({
    totalUniversities: 0,
    totalUsers: 0,
    pendingApprovals: 0,
    recentActivity: 0
  });
  const [universities, setUniversities] = useState([]);
  const [recentLogs, setRecentLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [activeUniversity]);

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const [universitiesData, usersData, auditData] = await Promise.all([
        University.list(),
        UserProfile.list(),
        AuditLog.list('created_at', false, 10)
      ]);

      let filteredUniversities = universitiesData || [];
      let filteredUsers = usersData || [];
      let filteredAuditLogs = auditData || [];

      if (activeUniversity) {
        filteredUniversities = universitiesData.filter(u => u.id === activeUniversity.id);
        filteredUsers = usersData.filter(u => u.university_id === activeUniversity.id);
        filteredAuditLogs = auditData.filter(log => log.university_id === activeUniversity.id);
      }

      const totalUniversities = filteredUniversities.length;
      const totalUsers = filteredUsers.length;
      const pendingApprovals = filteredUsers.filter(user => user.status === 'pending').length;
      const recentActivity = filteredAuditLogs.length;

      setStats({ totalUniversities, totalUsers, pendingApprovals, recentActivity });
      setUniversities(filteredUniversities);
      setRecentLogs(filteredAuditLogs);
    } catch (error) {
      console.error('Error loading super admin dashboard:', error);
    }
    setIsLoading(false);
  };

  return (
    <div className="p-6 space-y-8 bg-gray-200 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-neumorphic mb-2">Super Admin Dashboard</h1>
            <p className="text-gray-600">
              {activeUniversity ? (
                <>Viewing data for: <strong>{activeUniversity.name}</strong></>
              ) : (
                'Global system overview and management (All Universities)'
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to={createPageUrl("UniversityManagement")}>
              <Button className="neumorphic rounded-xl px-4 py-2 flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Add University
              </Button>
            </Link>
            <Link to={createPageUrl("AuditLogs")}>
              <Button className="neumorphic rounded-xl px-4 py-2 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                View Audit Logs
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCards 
            title="Universities" 
            value={stats.totalUniversities} 
            icon={Building} 
            gradient="from-blue-500 to-blue-600" 
            isLoading={isLoading}
            linkTo={createPageUrl("UniversityManagement")}
          />
          <StatsCards 
            title="Total Users" 
            value={stats.totalUsers} 
            icon={Users} 
            gradient="from-green-500 to-green-600" 
            isLoading={isLoading}
          />
          <StatsCards 
            title="Pending Approvals" 
            value={stats.pendingApprovals} 
            icon={Shield} 
            gradient="from-orange-500 to-orange-600" 
            isLoading={isLoading}
          />
          <StatsCards 
            title="Recent Activity" 
            value={stats.recentActivity} 
            icon={Activity} 
            gradient="from-purple-500 to-purple-600" 
            isLoading={isLoading}
            linkTo={createPageUrl("AuditLogs")}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Universities Overview */}
          <Card className="neumorphic rounded-3xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-neumorphic">Universities</CardTitle>
                <Link to={createPageUrl("UniversityManagement")}>
                  <Button size="sm" className="neumorphic rounded-xl">
                    <Settings className="w-4 h-4 mr-2" />
                    Manage
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  {Array(3).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-xl" />
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {universities.map(university => (
                    <div key={university.id} className="neumorphic-inset rounded-xl p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-neumorphic">{university.name}</h3>
                          <p className="text-sm text-gray-500">{university.domain}</p>
                        </div>
                        <Badge className={`${
                          university.status === 'active' ? 'bg-green-100 text-green-800' :
                          university.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        } border-0 capitalize`}>
                          {university.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="neumorphic rounded-3xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold text-neumorphic">Recent System Activity</CardTitle>
                <Link to={createPageUrl("AuditLogs")}>
                  <Button size="sm" className="neumorphic rounded-xl">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    View All
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {Array(5).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full rounded-xl" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {recentLogs.map(log => (
                    <div key={log.id} className="neumorphic-inset rounded-xl p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-neumorphic">
                            {log.action} {log.resource_type}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(log.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800 border-0 text-xs">
                          {log.action}
                        </Badge>
                      </div>
                    </div>
                  ))}
                  {recentLogs.length === 0 && (
                    <p className="text-center text-gray-500 py-8">No recent activity</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}